'use client';

import { Button } from '@ui/components/button';
import { Input } from '@ui/components/input';
import { Label } from '@ui/components/label';
import { text } from '@modules/garden-planner/text';
import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faMinus } from '@micks-yard/icons/solid/faMinus';
import { faPlus } from '@micks-yard/icons/solid/faPlus';

interface GridControlsViewProps {
  rows: number;
  columns: number;
  onAddRow: () => void;
  onRemoveRow: () => Promise<void>;
  onAddColumn: () => void;
  onRemoveColumn: () => Promise<void>;
  onRowsChange: (rows: number) => void;
  onColumnsChange: (columns: number) => void;
}

export function GridControlsView({
  rows,
  columns,
  onAddRow,
  onRemoveRow,
  onAddColumn,
  onRemoveColumn,
  onRowsChange,
  onColumnsChange,
}: GridControlsViewProps) {
  return (
    <div className="border-border mt-4 flex flex-col gap-3 border-t pt-4">
      <h2 className="text-foreground text-base font-semibold">{text.gridControls}</h2>

      <div className="flex flex-col gap-1.5">
        <Label className="text-muted-foreground text-sm font-medium">{text.rows}</Label>
        <div className="flex flex-row items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={rows <= 1}
            onClick={() => void onRemoveRow()}
            aria-label={text.removeRow}>
            <FaIcon icon={faMinus} className="size-4" />
          </Button>
          <Input
            className="h-7 flex-1 text-center"
            inputMode="numeric"
            value={String(rows)}
            onChange={(event) => {
              const parsed = parseInt(event.target.value, 10);
              if (!isNaN(parsed) && parsed >= 1) {
                onRowsChange(parsed);
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAddRow}
            aria-label={text.addRow}>
            <FaIcon icon={faPlus} className="size-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-muted-foreground text-sm font-medium">{text.columns}</Label>
        <div className="flex flex-row items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={columns <= 1}
            onClick={() => void onRemoveColumn()}
            aria-label={text.removeColumn}>
            <FaIcon icon={faMinus} className="size-4" />
          </Button>
          <Input
            className="h-7 flex-1 text-center"
            inputMode="numeric"
            value={String(columns)}
            onChange={(event) => {
              const parsed = parseInt(event.target.value, 10);
              if (!isNaN(parsed) && parsed >= 1) {
                onColumnsChange(parsed);
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAddColumn}
            aria-label={text.addColumn}>
            <FaIcon icon={faPlus} className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
